'use client';

import { useState, useMemo } from 'react';
import Card from '@/components/ui/Card';
import Spinner from '@/components/ui/Spinner';
import ErrorAlert from '@/components/ui/ErrorAlert';
import ESMStockFilterForm from '@/components/filters/ESMStockFilterForm';
import ESMStockTable from '@/components/esm-stock/ESMStockTable';
import { useESMStockFilterOptions } from '@/hooks/useESMStockFilterOptions';
import { useESMStocks } from '@/hooks/useESMStocks';
import BackButton from '@/components/ui/BackButton';
import { Search, TableIcon, Target, Zap, ArrowUpDown, AlertTriangle } from 'lucide-react';
import Link from 'next/link';
import PageLayout from '@/components/layout/PageLayout';
import type { ESMStockListItemFE } from '@/types/esmStock';

export default function ESMStockAnalysisPage() {
  const { filterOptions, loading: loadingFilters, error: filterError } = useESMStockFilterOptions();
  const { stocks, loading: loadingStocks, error: stocksError, fetchESMStocks } = useESMStocks();
  const [hasSearched, setHasSearched] = useState(false);
  const [peFilters, setPeFilters] = useState<{ pe_min?: number; pe_max?: number }>({});

  const handleFilterSubmit = async (filters: Record<string, unknown>) => {
    // Extract PE filters for frontend filtering
    const { pe_min, pe_max, ...backendFilters } = filters;
    
    // Store PE filters in local state
    setPeFilters({
      pe_min: pe_min ? Number(pe_min) : undefined,
      pe_max: pe_max ? Number(pe_max) : undefined,
    });
    
    // Send other filters to backend
    await fetchESMStocks(backendFilters);
    setHasSearched(true);
  };


  // Frontend filtering for PE ratio
  const filteredStocks = useMemo(() => {
    if (!stocks.length) return stocks;
    
    return stocks.filter((stock: ESMStockListItemFE) => {
      const pe = stock.price_to_earning;
      
      // If PE is null/undefined, exclude from filtered results when PE filters are applied
      if (pe === null || pe === undefined) {
        return !peFilters.pe_min && !peFilters.pe_max;
      }
      
      // Apply PE range filters
      if (peFilters.pe_min !== undefined && pe < peFilters.pe_min) {
        return false;
      }
      
      if (peFilters.pe_max !== undefined && pe > peFilters.pe_max) {
        return false;
      }
      
      return true;
    });
  }, [stocks, peFilters]);

  const error = filterError || stocksError;

  return (
    <PageLayout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 md:py-10">
        <header className="mb-8 flex items-start gap-3">
          <div className="md:mt-0.5">
            <BackButton />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">ESM Stock Analysis</h1>
            <p className="mt-1 text-sm md:text-base text-zinc-500 dark:text-zinc-400">
              Stocks under the Enhanced Surveillance Measure framework
            </p>
          </div>
        </header>

        {/* ESM Tools Entry Points */}
        <div className="mb-6 flex flex-wrap gap-3">
          <Link href="/esm-2to1-prediction" className="group inline-flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 transition-colors hover:border-emerald-500/40 hover:text-emerald-700 dark:hover:text-emerald-400">
            <Target className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            ESM 2-to-1 Exit Predictions
            <svg className="ml-1 w-4 h-4 text-zinc-400 transition-colors group-hover:text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
          <Link href="/esm-exited-stocks" className="group inline-flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 transition-colors hover:border-emerald-500/40 hover:text-emerald-700 dark:hover:text-emerald-400">
            <Zap className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            ESM Exited Stocks
            <svg className="ml-1 w-4 h-4 text-zinc-400 transition-colors group-hover:text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
          <Link href="/esm-2-to-1-stocks" className="group inline-flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 transition-colors hover:border-emerald-500/40 hover:text-emerald-700 dark:hover:text-emerald-400">
            <ArrowUpDown className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            ESM 2-to-1 Stocks
            <svg className="ml-1 w-4 h-4 text-zinc-400 transition-colors group-hover:text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
          <Link href="/esm-2-overdue-stocks" className="group inline-flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 transition-colors hover:border-emerald-500/40 hover:text-emerald-700 dark:hover:text-emerald-400">
            <AlertTriangle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            ESM 2 Overdue Stocks
            <svg className="ml-1 w-4 h-4 text-zinc-400 transition-colors group-hover:text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
        
        {error && <ErrorAlert message={error} className="mb-6" />}
        
        <main className="space-y-6">
          <Card className="p-0">
            <div className="flex items-center justify-between gap-3 border-b border-zinc-200 dark:border-zinc-800 px-6 py-4">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Filter Stocks</h2>
              </div>
              {hasSearched && filteredStocks.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-zinc-100 dark:bg-zinc-800 px-3 py-1 text-xs font-medium text-zinc-600 dark:text-zinc-400">
                    Found {filteredStocks.length} stock{filteredStocks.length !== 1 ? 's' : ''}
                    {filteredStocks.length !== stocks.length && (
                      <span className="text-xs text-zinc-500 dark:text-zinc-400 ml-1">
                        (of {stocks.length} total)
                      </span>
                    )}
                  </span>
                </div>
              )}
            </div>
            
            {loadingFilters ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="md" className="text-emerald-600" />
                <span className="ml-3 text-zinc-500 dark:text-zinc-400">Loading filters...</span>
              </div>
            ) : filterOptions ? (
              <ESMStockFilterForm 
                filterOptions={filterOptions} 
                onSubmit={handleFilterSubmit} 
                isLoading={loadingStocks}
              />
            ) : (
              <div className="text-center py-8 text-zinc-500 dark:text-zinc-400">
                Failed to load filter options. Please refresh the page.
              </div>
            )}
          </Card>
          
          {loadingStocks ? (
            <Card className="flex flex-col items-center justify-center py-16">
              <Spinner size="lg" className="text-emerald-600" />
              <span className="mt-4 text-zinc-500 dark:text-zinc-400">Loading stock data...</span>
            </Card>
          ) : (
            hasSearched && (
              <>
                {filteredStocks.length > 0 ? (
                  <Card>
                    <div className="flex justify-between items-center mb-6">
                      <div className="flex items-center gap-2">
                        <TableIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                        <h2 className="text-base font-semibold text-zinc-900 dark:text-white">
                          Stock Results
                        </h2>
                      </div>
                      <div className="text-sm text-zinc-500 dark:text-zinc-400">
                        Showing {filteredStocks.length} stock{filteredStocks.length !== 1 ? 's' : ''}
                        {filteredStocks.length !== stocks.length && (
                          <span className="text-xs text-zinc-500 dark:text-zinc-400 ml-1">
                            (filtered from {stocks.length} total)
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <ESMStockTable stocks={filteredStocks} />
                  </Card>
                ) : stocks.length > 0 ? (
                  <Card className="py-16 text-center">
                    <div>
                      <svg className="mx-auto h-12 w-12 text-zinc-300 dark:text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 className="mt-4 text-lg font-medium text-zinc-900 dark:text-white">No results after filtering</h3>
                      <p className="mt-3 text-zinc-500 dark:text-zinc-400 max-w-md mx-auto">
                        Found {stocks.length} stocks from your search, but none match your P/E ratio filter. Try adjusting the P/E range.
                      </p>
                    </div>
                  </Card>
                ) : (
                  <Card className="py-16 text-center">
                    <div>
                      <svg className="mx-auto h-12 w-12 text-zinc-300 dark:text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 className="mt-4 text-lg font-medium text-zinc-900 dark:text-white">No results found</h3>
                      <p className="mt-3 text-zinc-500 dark:text-zinc-400 max-w-md mx-auto">
                        No stocks found matching your criteria. Try adjusting your filters to broaden your search.
                      </p>
                    </div>
                  </Card>
                )}
              </>
            )
          )}

        </main>
      </div>
    </PageLayout>
  );
} 